/**
 * Sentry Span Filtering Configuration
 *
 * This file contains configuration for spans that should be excluded from Sentry.
 * Add patterns here to filter out high-frequency or noisy operations.
 */

export const ignoredSpanNames = [
  "partner.orders.fetch",
];

/**
 * Patterns to match against span descriptions
 * Supports exact matches and partial matches (includes)
 */
export const ignoredSpanDescriptions = [
  "/v2/orders/live", // Live orders API (high-frequency polling)
  "partner.orders.fetch", // Partner orders fetch operations
  "localStorage", // localStorage operations
  "Fetch Partner Orders",
];

/**
 * Span operations to exclude
 * These are Sentry span operation types (span.op)
 */
export const ignoredSpanOperations = [
  "db.write", // IndexedDB write operations
  "db.read", // IndexedDB read operations
  "db.query", // IndexedDB query operations
  "storage.local", // Local storage operations
];

/**
 * Attributes to check for exclusion
 * Key-value pairs where key is the attribute path and value is what to match
 */
export const ignoredSpanAttributes = [
  {
    key: "http.url",
    match: "/v2/orders/live", // HTTP URLs containing this path
  },
  {
    key: "db.system",
    match: "localStorage", // Database system type
  },
];

/**
 * Check if a span should be ignored based on description
 * @param {string} description - The span description
 * @returns {boolean} - True if span should be ignored
 */
export const shouldIgnoreByDescription = (description) => {
  if (!description) return false;

  return ignoredSpanDescriptions.some(
    (pattern) => description === pattern || description.includes(pattern)
  );
};

/**
 * Check if a span should be ignored based on operation type
 * @param {string} op - The span operation type
 * @returns {boolean} - True if span should be ignored
 */
export const shouldIgnoreByOperation = (op) => {
  if (!op) return false;

  return ignoredSpanOperations.includes(op);
};

/**
 * Check if a span should be ignored based on attributes
 * @param {object} attributes - The span attributes
 * @returns {boolean} - True if span should be ignored
 */
export const shouldIgnoreByAttributes = (attributes) => {
  if (!attributes) return false;

  return ignoredSpanAttributes.some(({ key, match }) => {
    const value = attributes[key];
    if (!value) return false;

    return (
      typeof value === "string" && (value === match || value.includes(match))
    );
  });
};

/**
 * Check if a span should be ignored based on name
 * @param {string} name - The span name
 * @returns {boolean} - True if span should be ignored
 */
export const shouldIgnoreByName = (name) => {
  if (!name) return false;
  return ignoredSpanNames.includes(name);
};

/**
 * Check if a span should be ignored
 * @param {object} span - The Sentry span object
 * @returns {boolean} - True if span should be ignored
 */
export const shouldIgnoreSpan = (span) => {
  if (!span) return false;

  return (
    shouldIgnoreByName(span.name) ||
    shouldIgnoreByDescription(span.description) ||
    shouldIgnoreByOperation(span.op) ||
    shouldIgnoreByAttributes(span.attributes)
  );
};
